import type { TFunction } from 'i18next';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BarChart3,
  Bell,
  BookOpen,
  Bookmark,
  Bot,
  Boxes,
  Clock,
  Compass,
  CreditCard,
  DollarSign,
  Download,
  FileText,
  Globe,
  GraduationCap,
  HardDrive,
  Heart,
  Highlighter,
  History,
  Home,
  Layers,
  LayoutTemplate,
  Link2,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  Puzzle,
  Radio,
  RotateCcw,
  Settings,
  Shield,
  Shirt,
  ShoppingBag,
  Sparkles,
  Store,
  Terminal,
  TrendingUp,
  User,
  UserPlus,
  Users,
  Video,
} from 'lucide-react';

export interface DashboardNavEntry {
  href: string;
  label: string;
  Icon: LucideIcon;
  badge?: number | 'dot';
}

export interface DashboardNavGroup {
  id: 'content' | 'create' | 'community' | 'business' | 'insights' | 'tools';
  label: string;
  items: DashboardNavEntry[];
}

/**
 * One source of truth for dashboard information architecture.
 *
 * This intentionally includes meaningful top-level destinations only. Route
 * aliases, redirects, detail/edit screens, and shop/site child routes stay
 * inside their owning surface instead of turning global navigation into a
 * sitemap. The same groups power desktop More, the mobile drawer, and search.
 */
export function buildDashboardNavGroups(t: TFunction): DashboardNavGroup[] {
  return [
    {
      id: 'content',
      label: t('nav.library', 'My Library'),
      items: [
        { href: '/dashboard/my-posts', label: t('myPosts.title', 'My Posts'), Icon: FileText },
        { href: '/dashboard/my-articles', label: t('nav.myArticles', 'My Articles'), Icon: BookOpen },
        { href: '/dashboard/media', label: t('nav.media', 'Media'), Icon: HardDrive },
        { href: '/dashboard/library', label: t('nav.allSaved', 'All Saved'), Icon: Boxes },
        { href: '/dashboard/saved', label: t('nav.saved', 'Saved'), Icon: Bookmark },
        { href: '/dashboard/library/tags', label: t('nav.tags', 'Tags'), Icon: Highlighter },
      ],
    },
    {
      id: 'create',
      label: t('nav.create', 'Create'),
      items: [
        { href: '/dashboard/write', label: t('homeNext.makeWrite', 'Write'), Icon: FileText },
        { href: '/dashboard/collections', label: t('nav.sites', 'Sites'), Icon: Globe },
        { href: '/dashboard/templates', label: t('nav.templates', 'Templates'), Icon: LayoutTemplate },
        { href: '/dashboard/my-templates', label: t('nav.myTemplates', 'My Templates'), Icon: Package },
        { href: '/dashboard/prompts', label: t('nav.promptTemplates', 'Prompt Templates'), Icon: MessageSquare },
        { href: '/dashboard/my-prompts', label: t('myPrompts.title', 'My Prompts'), Icon: FileText },
        { href: '/dashboard/commerce', label: t('nav.shops', 'Shops'), Icon: ShoppingBag },
        { href: '/dashboard/teaching', label: t('nav.teaching', 'Teaching'), Icon: GraduationCap },
      ],
    },
    {
      id: 'community',
      label: t('nav.social', 'Social'),
      items: [
        { href: '/dashboard/agent', label: t('nav.agent', 'My Agent'), Icon: Bot },
        { href: '/dashboard/agents', label: t('nav.agents', 'Public Agents'), Icon: Sparkles },
        { href: '/dashboard/people', label: t('nav.people', 'People'), Icon: Users },
        { href: '/dashboard/friends', label: t('nav.friends', 'Friends'), Icon: Users },
        { href: '/dashboard/following', label: t('nav.following', 'Following'), Icon: UserPlus },
        { href: '/dashboard/channels', label: t('channels.title', 'Channels'), Icon: Radio },
        { href: '/dashboard/rooms', label: t('nav.rooms', 'Rooms'), Icon: Globe },
        { href: '/dashboard/live-streams', label: t('nav.myStreams', 'My Streams'), Icon: Video },
        { href: '/dashboard/watch-party', label: t('nav.watchParty', 'Watch Party'), Icon: Video },
        { href: '/dashboard/invitations', label: t('nav.invitations', 'Invitations'), Icon: UserPlus },
      ],
    },
    {
      id: 'business',
      label: t('nav.businessDashboard', 'Business'),
      items: [
        { href: '/dashboard/commerce-hub', label: t('nav.commerce', 'Commerce'), Icon: Store },
        { href: '/dashboard/commerce', label: t('nav.myShop', 'My Shop'), Icon: ShoppingBag },
        // Outfits lives here, not under Create: the V2 sidebar hides the Create
        // group behind the "+ Create" popover, where nobody looked for it.
        { href: '/dashboard/outfits', label: t('nav.outfits', 'Outfits'), Icon: Shirt },
        // Was labelled "Marketplace", which people read as the outfit
        // marketplace; the page is the shop directory (ExploreShops).
        { href: '/dashboard/marketplace', label: t('myOrders.exploreShops', 'Explore Shops'), Icon: Store },
        { href: '/dashboard/shop', label: t('nav.shopping', 'Shopping'), Icon: ShoppingBag },
        { href: '/dashboard/my-orders', label: t('nav.myOrders', 'My Orders'), Icon: ShoppingBag },
        { href: '/dashboard/my-returns', label: t('nav.returns', 'Returns'), Icon: RotateCcw },
        { href: '/dashboard/my-addresses', label: t('nav.addresses', 'Addresses'), Icon: MapPin },
        { href: '/dashboard/my-downloads', label: t('nav.downloads', 'Downloads'), Icon: Download },
        { href: '/dashboard/my-store-credits', label: t('nav.storeCredits', 'Store Credits'), Icon: CreditCard },
        { href: '/dashboard/wishlist', label: t('nav.wishlist', 'Wishlist'), Icon: Heart },
        { href: '/dashboard/my-courses', label: t('nav.myCourses', 'My Courses'), Icon: GraduationCap },
        { href: '/dashboard/live', label: t('nav.liveShopping', 'Live Shopping'), Icon: Radio },
        { href: '/dashboard/affiliates', label: t('nav.affiliates', 'Affiliates'), Icon: Link2 },
        { href: '/dashboard/business', label: t('nav.businessDashboard', 'Business'), Icon: Store },
        { href: '/dashboard/creator', label: t('nav.creator', 'Creator'), Icon: User },
        { href: '/dashboard/creator-fund', label: t('nav.creatorFund', 'Creator Fund'), Icon: DollarSign },
      ],
    },
    {
      id: 'insights',
      label: t('nav.analytics', 'Analytics'),
      items: [
        { href: '/dashboard/analytics', label: t('nav.analytics', 'Analytics'), Icon: BarChart3 },
        { href: '/dashboard/visitor-analytics', label: t('visitorAnalytics.title', 'Visitor Analytics'), Icon: Activity },
        { href: '/dashboard/creator-analytics', label: t('creatorAnalytics.creatorAnalytics', 'Creator Analytics'), Icon: TrendingUp },
        { href: '/dashboard/outfits/analytics', label: t('outfitAnalytics.title', 'Outfit analytics'), Icon: Shirt },
        { href: '/dashboard/activity', label: t('nav.activity', 'Activity'), Icon: Activity },
        { href: '/dashboard/scoop-score', label: t('nav.scoopScore', 'Post Score'), Icon: BarChart3 },
      ],
    },
    {
      id: 'tools',
      label: t('nav.workspace', 'Workspace'),
      items: [
        { href: '/dashboard/workspace', label: t('nav.workspace', 'Workspace'), Icon: Layers },
        { href: '/dashboard/bookmarks', label: t('nav.bookmarks', 'Bookmarks'), Icon: Bookmark },
        { href: '/dashboard/reading-list', label: t('nav.readingList', 'Reading List'), Icon: BookOpen },
        { href: '/dashboard/history', label: t('nav.history', 'History'), Icon: History },
        { href: '/dashboard/tabs', label: t('nav.openTabs', 'Open Tabs'), Icon: Clock },
        { href: '/dashboard/annotations', label: t('nav.annotations', 'Annotations'), Icon: Highlighter },
        { href: '/dashboard/ai', label: t('nav.askAi', 'Ask AI'), Icon: Bot },
        { href: '/dashboard/addons', label: t('nav.addons', 'Add-ons'), Icon: Puzzle },
        { href: '/dashboard/my-plugins', label: t('myPlugins.myPlugins', 'My Plugins'), Icon: Puzzle },
        { href: '/dashboard/developer', label: t('nav.developer', 'Developer'), Icon: Terminal },
        { href: '/dashboard/credits', label: t('credits.title', 'Credits'), Icon: Sparkles },
        { href: '/dashboard/pricing', label: t('nav.pricing', 'Pricing'), Icon: CreditCard },
        { href: '/dashboard/security', label: t('nav.security', 'Security'), Icon: Shield },
        { href: '/dashboard/settings', label: t('nav.settings', 'Settings'), Icon: Settings },
      ],
    },
  ];
}

export function buildPrimaryDashboardNav(
  t: TFunction,
  unreadMessages = 0,
  unreadNotifications = 0,
): DashboardNavEntry[] {
  return [
    { href: '/dashboard', label: t('nav.home', 'Home'), Icon: Home },
    // Studio is the flagship creation workspace, not a secondary tool. Keep
    // it permanently visible while its Image/Video shortcuts remain in Create.
    { href: '/dashboard/studio', label: t('nav.studio', 'Studio'), Icon: Sparkles },
    { href: '/dashboard/discover', label: t('nav.discover', 'Discover'), Icon: Compass },
    { href: '/dashboard/communities', label: t('nav.communities', 'Communities'), Icon: Globe },
    { href: '/dashboard/chats', label: t('nav.chats', 'Chats'), Icon: Mail, badge: unreadMessages },
    { href: '/dashboard/notifications', label: t('nav.notifications', 'Notifications'), Icon: Bell, badge: unreadNotifications },
  ];
}
